import {
  ApiError,
  projectPlanningControllerArchiveWorkOrders,
  projectPlanningControllerBatchAddWorkOrders,
  projectPlanningControllerBatchRemoveWorkOrders,
  projectPlanningControllerCreateProjectPlanning,
  projectPlanningControllerGetProjectPlanning,
  projectPlanningControllerGetProjectLaborShiftSummary,
  projectPlanningControllerListProjectsPlanning,
  projectPlanningControllerPreviewReleaseRoutes,
  projectPlanningControllerRedraftProject,
  projectPlanningControllerReleaseDraftWorkOrders,
  projectPlanningControllerReleaseProject,
  projectPlanningControllerUnarchiveWorkOrders,
  projectPlanningControllerUpdateProjectColumnConfig,
  projectPlanningControllerUpdateProjectPlanning,
  projectsControllerArchiveProject,
  projectsControllerUnarchiveProject,
  workOrdersPartnerControllerReopenForEdit,
  workOrdersPartnerControllerReRelease,
  type ProjectArchiveStateDto,
  type RedraftProjectDto,
  type ProjectLaborShiftSummaryDto,
  type ReleaseRoutePreviewDto,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { expectData } from '../_shared/api';
import { OPERATING_COSTS_KEY } from '../operating-costs/queries';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import { clientIdHeaders } from '../realtime/client-id';
import { RUN_SHEETS_KEY } from '../run-sheets/queries';
import {
  type BatchProgress,
  PROJECT_PLANNING_BATCH_CHUNK_SIZE,
  WORK_ORDER_STATUS_TRANSITION_CHUNK_SIZE,
} from './constants';
import { PROJECTS_KEY } from './project-queries';
import {
  applyStatusTransitionResult,
  chunkWorkOrderIds,
  type StatusTransitionResult,
} from './status-transition';
import type {
  ProjectColumnState,
  ProjectPlanningDetail,
  ProjectPlanningListItem,
  ProjectWorkOrderDetail,
} from './types';

export const PROJECT_PLANNING_KEY = 'project-planning' as const;

function invalidateProjectLaborSummary(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: [PROJECT_PLANNING_KEY, 'labor-summary', projectId],
  });
}

// ─── Project planning list ───────────────────────────────────────

export function projectPlanningListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'list', partnerId] as const,
    queryFn: async ({ signal }) => {
      const res = await projectPlanningControllerListProjectsPlanning(
        { partner_id: partnerId ?? '' },
        { signal },
      );
      return res.data as ProjectPlanningListItem[];
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Project planning detail ─────────────────────────────────────

export function projectPlanningDetailQueryOptions(projectId: string | undefined) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId] as const,
    queryFn: async ({ signal }) => {
      if (!projectId) throw new Error('projectId is required');
      const res = await projectPlanningControllerGetProjectPlanning(projectId, undefined, {
        signal,
      });
      return res.data as ProjectPlanningDetail;
    },
    enabled: !!projectId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function projectLaborShiftSummaryQueryOptions(projectId: string | undefined) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'labor-summary', projectId] as const,
    queryFn: async ({ signal }) => {
      if (!projectId) throw new Error('projectId is required');
      const response = await projectPlanningControllerGetProjectLaborShiftSummary(projectId, {
        signal,
      });
      return expectData<ProjectLaborShiftSummaryDto>(response);
    },
    enabled: !!projectId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Release route preview (ENG-822 §12.5) ───────────────────────
//
// Which work orders will release WITHOUT a route because their factory's
// default route can't build their product type, grouped by product for the
// Stage 4 warning. Read-only and never blocking — release proceeds either way
// (§12.2), so this is advisory copy, not a gate.
//
// Short staleTime because the answer moves with route settings and with the
// planner's own factory edits: the user can be editing the grid in one tab and
// reviewing the release in another.

export function releaseRoutePreviewQueryOptions(projectId: string | undefined) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'release-route-preview', projectId] as const,
    queryFn: async ({ signal }) => {
      if (!projectId) throw new Error('projectId is required');
      const res = await projectPlanningControllerPreviewReleaseRoutes(projectId, { signal });
      return expectData<ReleaseRoutePreviewDto>(res);
    },
    enabled: !!projectId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Archived work orders (additive, for "Show archived") ────────
//
// Kept separate from the main detail query so the editor's optimistic
// updates (use-project-sync, batch add/remove) keep writing to the
// untouched `[KEY, 'detail', projectId]` cache entry. The archived rows
// are fetched only when the toggle is on and merged in for display. The
// queryKey nests under the detail prefix so the archive/unarchive
// mutations' `[KEY, 'detail', projectId]` invalidation refreshes it too.

export function projectArchivedWorkOrdersQueryOptions(
  projectId: string | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId, 'archived'] as const,
    queryFn: async ({ signal }) => {
      if (!projectId) throw new Error('projectId is required');
      const res = await projectPlanningControllerGetProjectPlanning(
        projectId,
        { include_archived: true },
        { signal },
      );
      const detail = res.data as ProjectPlanningDetail;
      return detail.work_orders.filter((wo) => wo.work_order_current_status === 'archived');
    },
    enabled: !!projectId && enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Create project mutation ─────────────────────────────────────

export interface CreateProjectParams {
  partner_id: string;
  project_name: string;
  project_code: string;
  project_prefix?: string | null;
  project_scheduled_start_date?: string | null;
  project_scheduled_complete_date?: string | null;
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateProjectParams) => {
      const res = await projectPlanningControllerCreateProjectPlanning(params);
      return res.data as ProjectPlanningListItem;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list', variables.partner_id],
      });
    },
  });
}

// ─── Batch add work orders ───────────────────────────────────────

export interface BatchAddWorkOrdersParams {
  count: number;
  /** container_option_id of the Product Type applied to every new row */
  product_type_option_id: string;
}

export function useBatchAddWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: BatchAddWorkOrdersParams) => {
      const total = params.count;
      setProgress({ done: 0, total });

      let latest: ProjectWorkOrderDetail[] = [];
      let done = 0;
      while (done < total) {
        const chunkCount = Math.min(PROJECT_PLANNING_BATCH_CHUNK_SIZE, total - done);
        const res = await projectPlanningControllerBatchAddWorkOrders(
          projectId,
          {
            ...params,
            count: chunkCount,
          },
          clientIdHeaders(),
        );
        latest = res.data as ProjectWorkOrderDetail[];
        done += chunkCount;
        setProgress({ done, total });
        queryClient.setQueryData<ProjectPlanningDetail>(
          [PROJECT_PLANNING_KEY, 'detail', projectId],
          (old) => (old ? { ...old, work_orders: latest } : old),
        );
      }
      return latest;
    },
    onSettled: () => {
      setProgress(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'labor-summary', projectId],
      });
      // Add-rows may have minted a brand-new product via
      // findOrCreateProductForSelections + createProductWithBoOTx. The
      // project-scoped Run Sheet view filters its partner-wide matrix by the
      // project's work-order product_ids; if the matrix is still cached
      // from before this add, the new product won't appear.
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix'],
      });
      // The partner-wide Production Planner enumerates the same work orders;
      // refresh it so the added rows show up there.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });

  return Object.assign(mutation, { progress });
}

// ─── Batch remove work orders ────────────────────────────────────

export function useBatchRemoveWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const total = workOrderIds.length;
      setProgress({ done: 0, total });

      let latest: ProjectWorkOrderDetail[] = [];
      for (let i = 0; i < total; i += PROJECT_PLANNING_BATCH_CHUNK_SIZE) {
        const chunk = workOrderIds.slice(i, i + PROJECT_PLANNING_BATCH_CHUNK_SIZE);
        const res = await projectPlanningControllerBatchRemoveWorkOrders(
          projectId,
          {
            work_order_ids: chunk,
          },
          clientIdHeaders(),
        );
        latest = res.data as ProjectWorkOrderDetail[];
        setProgress({ done: Math.min(i + chunk.length, total), total });
        queryClient.setQueryData<ProjectPlanningDetail>(
          [PROJECT_PLANNING_KEY, 'detail', projectId],
          (old) => (old ? { ...old, work_orders: latest } : old),
        );
      }
      return latest;
    },
    onSettled: () => {
      setProgress(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'labor-summary', projectId],
      });
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix'],
      });
      // Removed rows must disappear from the partner-wide Production Planner.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
    },
  });

  return Object.assign(mutation, { progress });
}

// ─── Update project metadata ─────────────────────────────────────
//
// Used by the Edit Project dialog. The backend rejects edits on
// released projects with 409; the mutation surfaces that distinctly so
// the dialog can keep itself open and explain why.

export interface UpdateProjectPlanningParams {
  project_name?: string;
  project_code?: string;
  project_prefix?: string | null;
  project_scheduled_start_date?: string | null;
  project_scheduled_complete_date?: string | null;
}

export class ProjectNotEditableError extends Error {
  constructor() {
    super('Only draft or redrafting projects can be edited');
    this.name = 'ProjectNotEditableError';
  }
}

export function useUpdateProjectPlanning(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateProjectPlanningParams) => {
      try {
        const res = await projectPlanningControllerUpdateProjectPlanning(projectId, params);
        return res.data as ProjectPlanningDetail;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 409) {
          throw new ProjectNotEditableError();
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      // project_name and other project-level fields are joined into the
      // partner-wide planner rows; refresh PP so renamed projects show their
      // new name without waiting for the 60s staleTime.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'labor-summary', projectId],
      });
    },
  });
}

// ─── Update project column layout ────────────────────────────────
//
// Persists the AG Grid column state for the project planning grid.
// Allowed on any project status — column layout is presentation-only.
//
// Optimistic concurrency: the server compares `expected_column_config_updated_at`
// to the row's current `project_column_config_updated_at` and returns 409 if
// another writer has saved since the client last read. On 409 we just
// invalidate the detail query — the editor's live grid state is held in a
// snapshot ref that's deliberately decoupled from the cache, so the user's
// in-progress layout isn't disturbed and the next save will carry the
// refreshed token.

export class ProjectColumnConfigConflictError extends Error {
  constructor() {
    super('Project column config was updated by another user');
    this.name = 'ProjectColumnConfigConflictError';
  }
}

export interface UpdateProjectColumnConfigParams {
  column_state: ProjectColumnState[];
  expected_column_config_updated_at: string;
}

export function useUpdateProjectColumnConfig(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateProjectColumnConfigParams) => {
      try {
        const res = await projectPlanningControllerUpdateProjectColumnConfig(projectId, params);
        return res.data as ProjectPlanningDetail;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 409) {
          throw new ProjectColumnConfigConflictError();
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
    },
    onError: (err) => {
      if (err instanceof ProjectColumnConfigConflictError) {
        // Refresh the cached detail so the next save sends the up-to-date
        // token. We deliberately do NOT touch the AG Grid state here — the
        // editor's snapshot ref absorbs the user's in-progress layout and
        // isn't bound to this cache entry, so this never causes a flip-back.
        void queryClient.invalidateQueries({
          queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId],
        });
      }
    },
  });
}

// ─── Release project ─────────────────────────────────────────────
//
// ENG-757 retired the workload-category release gate: release no longer takes
// a body and never blocks on confirmations.

export function useReleaseProject(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await projectPlanningControllerReleaseProject(projectId, clientIdHeaders());
      return res.data as ProjectPlanningDetail;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix'],
      });
      // Release flips every work order's status from `draft` → `scheduled`;
      // refresh the partner-wide planner so status chips and editability
      // re-derive immediately.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
      invalidateProjectLaborSummary(queryClient, projectId);
    },
  });
}

// Releases the work orders still in `draft` on an already-released
// (redrafting) project — the rows added during a redraft cycle. The backend
// validates required fields (400 MISSING_REQUIRED_FIELDS, same shape as
// releaseProject), renumbers each factory's sequence, and flips them
// `draft` → `scheduled`. Used by the re-release flow alongside
// `useReReleaseWorkOrders`.
export function useReleaseDraftWorkOrders(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await projectPlanningControllerReleaseDraftWorkOrders(
        projectId,
        clientIdHeaders(),
      );
      return res.data as ProjectPlanningDetail;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix'],
      });
      // Releasing drafts flips their status to `scheduled`; refresh the
      // partner-wide planner so status chips and editability re-derive.
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
      invalidateProjectLaborSummary(queryClient, projectId);
    },
  });
}

// ─── Reopen / re-release work orders ─────────────────────────────
//
// Released work orders ('scheduled') can be reopened for system-column edits,
// which flips them to 'redrafting'. Re-release sends them back to 'scheduled'.
// Both are bulk endpoints scoped by partner_id. The planner invalidates the
// detail query on success so the row badges and editability re-derive.

// Shared chunked-status-transition shape used by both reopen-for-edit and
// re-release. Payloads are split into `WORK_ORDER_STATUS_TRANSITION_CHUNK_SIZE`
// (500, the backend DTO's @maxItems cap) slices and fired concurrently, so a
// large project transitions in the fewest parallel round-trips instead of a
// serial per-chunk wait. Progress advances as each chunk settles. If any chunk
// rejects the mutation throws, carrying the first rejection's message plus how
// many WOs succeeded (fulfilled chunks stay committed on the backend) so the
// toast can surface it.
function useChunkedStatusTransition(
  projectId: string,
  partnerId: string,
  endpointSegment: 'reopen-for-edit' | 're-release',
) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const total = workOrderIds.length;
      const aggregated: StatusTransitionResult = { updated: [], unchanged: [] };
      if (total === 0) return aggregated;

      const chunks = chunkWorkOrderIds(workOrderIds, WORK_ORDER_STATUS_TRANSITION_CHUNK_SIZE);
      setProgress({ done: 0, total });

      let settled = 0;
      const outcomes = await Promise.allSettled(
        chunks.map(async (chunk) => {
          try {
            const res =
              endpointSegment === 'reopen-for-edit'
                ? await workOrdersPartnerControllerReopenForEdit(
                    partnerId,
                    { work_order_ids: chunk },
                    clientIdHeaders(),
                  )
                : await workOrdersPartnerControllerReRelease(
                    partnerId,
                    { work_order_ids: chunk },
                    clientIdHeaders(),
                  );
            return res.data as StatusTransitionResult;
          } finally {
            // Progress ticks as each chunk settles (fulfilled OR rejected), so
            // the bar always reaches the count of dispatched work orders.
            settled = Math.min(settled + chunk.length, total);
            setProgress({ done: settled, total });
          }
        }),
      );

      let fulfilledWorkOrders = 0;
      let firstRejection: { reason: unknown } | null = null;
      for (let i = 0; i < outcomes.length; i++) {
        const outcome = outcomes[i];
        const chunk = chunks[i];
        if (!outcome || !chunk) continue;
        if (outcome.status === 'fulfilled') {
          aggregated.updated.push(...outcome.value.updated);
          aggregated.unchanged.push(...outcome.value.unchanged);
          fulfilledWorkOrders += chunk.length;
        } else if (firstRejection === null) {
          firstRejection = { reason: outcome.reason };
        }
      }

      if (firstRejection !== null) {
        const baseMessage =
          firstRejection.reason instanceof Error
            ? firstRejection.reason.message
            : String(firstRejection.reason);
        throw new Error(
          `${baseMessage} — completed ${fulfilledWorkOrders} of ${total} work order${total === 1 ? '' : 's'} before the failure`,
        );
      }

      return aggregated;
    },
    onSuccess: (result) => {
      setProgress(null);
      // Patch the returned statuses straight onto the cached detail (the
      // endpoints only flip status, never renumber sequences), so the grid
      // updates without a full refetch. Fall back to invalidation when there's
      // no cached detail or a status literal is outside the known union.
      const detailKey = [PROJECT_PLANNING_KEY, 'detail', projectId] as const;
      const cached = queryClient.getQueryData<ProjectPlanningDetail>(detailKey);
      const patched = cached ? applyStatusTransitionResult(cached, result) : null;
      if (patched) {
        queryClient.setQueryData(detailKey, patched);
      }
      // The patch only carries work-order statuses, but ENG-830 also flips the
      // OWNING project's `project_status` — which the state chip, stepper and
      // toolbar now read directly instead of deriving from these rows. So the
      // detail must refetch or a subset redraft leaves them reading 'released'.
      // Patching first keeps the grid stable while that lands.
      void queryClient.invalidateQueries({ queryKey: detailKey });
      // Reopen-for-edit / re-release also flip the owning project's
      // project_status on entry/exit (ENG-830) — refresh the list so its
      // status chip picks up draft ⇄ released ⇄ redrafting; the partner-wide
      // planner uses the flipped work_order_current_status for its status
      // chips and editability.
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
      invalidateProjectLaborSummary(queryClient, projectId);
    },
    onError: () => {
      setProgress(null);
      // A partial run (some chunks committed before another rejected) really
      // did transition WOs on the backend, so refetch the authoritative detail
      // rather than patching an incomplete result.
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId],
      });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
      invalidateProjectLaborSummary(queryClient, projectId);
    },
  });

  return Object.assign(mutation, { progress });
}

export function useReopenForEdit(projectId: string, partnerId: string) {
  return useChunkedStatusTransition(projectId, partnerId, 'reopen-for-edit');
}

export function useReReleaseWorkOrders(projectId: string, partnerId: string) {
  return useChunkedStatusTransition(projectId, partnerId, 're-release');
}

// ─── Redraft a released project (ENG-830) ────────────────────────
//
// Whole-project counterpart to reopen-for-edit above: sets project_status to
// 'redrafting' unconditionally — including when the project has nothing
// eligible to reopen (empty project, or every row archived / in_progress /
// complete) — then reopens the requested `scheduled` rows in the same call.
// `workOrderIds` mirrors the DTO's three-way contract: omitted reopens every
// eligible row server-side (no 500 cap, no chunking needed), an explicit `[]`
// flips project_status only, and a subset reopens just those ids.

export function useRedraftProject(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workOrderIds?: string[]) => {
      const body: RedraftProjectDto =
        workOrderIds === undefined ? {} : { work_order_ids: workOrderIds };
      const res = await projectPlanningControllerRedraftProject(projectId, body, clientIdHeaders());
      return expectData<ProjectPlanningDetail>(res);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PROJECT_PLANNING_KEY, 'detail', projectId], data);
      void queryClient.invalidateQueries({
        queryKey: [PROJECT_PLANNING_KEY, 'list'],
      });
      // The partner-wide planner's editability and status chips key off
      // project_status too (canAddWorkOrders' backend counterpart).
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTION_PLANNER_KEY],
      });
      invalidateProjectLaborSummary(queryClient, projectId);
    },
  });
}

// ─── Archive / Unarchive work orders ─────────────────────────────
//
// Archiving soft-deletes scheduled/redrafting work orders (the backend
// enforces the status rule and reports anything it skipped). Unarchive is the
// inverse. Both invalidate the detail query (prefix match covers the archived
// and non-archived cache variants), the project list, the Run Sheet matrix (the
// "behind" / unreleased counts shift), and the partner-wide planner.

interface SkippedWorkOrder {
  work_order_id: string;
  work_order_current_status: string;
}

export interface ArchiveWorkOrdersResult {
  archived_ids: string[];
  skipped: SkippedWorkOrder[];
}

export interface UnarchiveWorkOrdersResult {
  unarchived_ids: string[];
  skipped: SkippedWorkOrder[];
}

function invalidateAfterArchiveChange(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId] });
  void queryClient.invalidateQueries({
    queryKey: [PROJECT_PLANNING_KEY, 'labor-summary', projectId],
  });
  void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
  void queryClient.invalidateQueries({ queryKey: [RUN_SHEETS_KEY, 'matrix'] });
  void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
}

export function useArchiveWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const res = await projectPlanningControllerArchiveWorkOrders(
        projectId,
        {
          work_order_ids: workOrderIds,
        },
        clientIdHeaders(),
      );
      return res.data as ArchiveWorkOrdersResult;
    },
    onSettled: () => {
      invalidateAfterArchiveChange(queryClient, projectId);
    },
  });
}

export function useUnarchiveWorkOrders(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workOrderIds: string[]) => {
      const res = await projectPlanningControllerUnarchiveWorkOrders(
        projectId,
        {
          work_order_ids: workOrderIds,
        },
        clientIdHeaders(),
      );
      return res.data as UnarchiveWorkOrdersResult;
    },
    onSettled: () => {
      invalidateAfterArchiveChange(queryClient, projectId);
    },
  });
}

// ─── Archive / unarchive a project (ENG-981) ─────────────────────
//
// The server owns the rule (no work order outside draft) and answers 409 with
// the reason, so a list that went stale between load and click still fails
// with something a toast can say. Archiving writes no work orders, so there is
// no realtime echo to stamp.

function invalidateAfterProjectArchiveChange(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
) {
  void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
  void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail', projectId] });
  // The project pickers (Run Sheets filter, Support Log) read these two lists
  // and hide archived projects.
  void queryClient.invalidateQueries({ queryKey: [PROJECTS_KEY, 'list'] });
  void queryClient.invalidateQueries({ queryKey: [OPERATING_COSTS_KEY, 'projects'] });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) =>
      expectData<ProjectArchiveStateDto>(await projectsControllerArchiveProject(projectId)),
    onSettled: (_data, _error, projectId) => {
      invalidateAfterProjectArchiveChange(queryClient, projectId);
    },
  });
}

export function useUnarchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) =>
      expectData<ProjectArchiveStateDto>(await projectsControllerUnarchiveProject(projectId)),
    onSettled: (_data, _error, projectId) => {
      invalidateAfterProjectArchiveChange(queryClient, projectId);
    },
  });
}
